/**
 * AppsList — the apps dashboard.
 *
 * Visuals match the `.dash` mockup in docs/newdesign.html:
 *   - top bar (Logo · Search · "+ New app" · user chip · Sign out)
 *   - card grid: `repeat(auto-fill, minmax(220px, 1fr))`, 16px gap
 *   - each card: 140px gradient preview · name · "Edited X · iOS+Android"
 *   - hover-revealed kebab menu for Rename / Duplicate / Delete
 *   - dashed-border "Create new app" placeholder card always rendered last
 *
 * All data comes from `/api/apps` on tile-backend (proxied by the gateway).
 * Click a card → editor at `/?platform=web&v2=true&appId=…`.
 */
import React, {
  FormEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AuthRoute, { AuthUser } from '@/app/routing/guards/auth-route/AuthRoute';
import AppShell from '@/features/dashboard/components/app-shell/AppShell';
import PushToGithubModal from '@/features/editor/modals/push-to-github/PushToGithubModal';
import { useOrgs } from '@/features/org/hooks/use-orgs/useOrgs';

/** Blueprint metadata returned by GET /api/blueprints. */
interface BlueprintMetadata {
  id: string;
  name: string;
  description: string | null;
  accent: string | null;
  emoji: string | null;
}

interface AppRow {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  /** Total Release rows for this app (any status). 0 by default for new apps. */
  releaseCount?: number;
  /** Releases currently in status='deployed'. */
  deployedCount?: number;
  /** True iff at least one release is deployed. App is "live" when this is true. */
  isLive?: boolean;
  /**
   * True if any cloud save on a branch is based on a git SHA that no
   * longer matches the cached Branch mirror's head_sha. Hint for the
   * dashboard that this app has unreconciled git drift; the full check
   * (live GitHub call) runs on branch-open in the editor.
   */
  hasDrift?: boolean;
}

interface GithubConnection {
  connected: boolean;
  repo: string;
  defaultBranch: string;
  tokenScope?: string;
  githubLogin?: string;
}

type ModalState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'rename'; app: AppRow }
  | { kind: 'delete'; app: AppRow }
  | { kind: 'github-disconnect'; app: AppRow }
  | { kind: 'github-push'; app: AppRow };

/* ────────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

function fmtRelative(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const sec = Math.round(ms / 1000);
    if (sec < 60) return `${sec} sec ago`;
    const min = Math.round(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hrs = Math.round(min / 60);
    if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
    const days = Math.round(hrs / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

/** Pick a stable gradient per app id so each card looks distinct without
 * needing a thumbnail. The palette comes from the design's mockup cards. */
const PREVIEW_GRADIENTS = [
  'linear-gradient(135deg, #eef2ff, #dbeafe)', // blue
  'linear-gradient(135deg, #ecfdf5, #d1fae5)', // mint
  'linear-gradient(135deg, #fefce8, #fef9c3)', // yellow
  'linear-gradient(135deg, #fdf2f8, #fce7f3)', // pink
  'linear-gradient(135deg, #f5f3ff, #e9d5ff)', // lilac
  'linear-gradient(135deg, #fff7ed, #ffedd5)', // peach
];
function gradientForId(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PREVIEW_GRADIENTS[h % PREVIEW_GRADIENTS.length];
}

/* ────────────────────────────────────────────────────────────────────────── */
/* AppsListInner                                                              */
/* ────────────────────────────────────────────────────────────────────────── */

function AppsListInner({ user }: { user: AuthUser }) {
  const [apps, setApps] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });
  const [search, setSearch] = useState('');
  /** appId → GitHub connection state (parallel fetched after apps load). */
  const [githubByApp, setGithubByApp] = useState<Record<string, GithubConnection>>({});
  /** appId currently in-flight for connect/disconnect — disables the menu. */
  const [githubBusyAppId, setGithubBusyAppId] = useState<string | null>(null);

  // Active-org slug drives the fetch: switching orgs must refetch the app
  // list (backend filters /api/apps by X-Active-Org, which is written
  // from the same cookie). Depending on activeSlug in the useEffect below
  // is what makes the switcher actually swap the apps grid.
  const { activeSlug } = useOrgs();

  const refresh = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/apps', { credentials: 'same-origin' });
      if (!res.ok) {
        setError(`Failed to load apps (HTTP ${res.status})`);
        return;
      }
      const data = (await res.json()) as { apps: AppRow[] };
      setApps(data.apps || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }, [activeSlug]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Fetch GitHub connection state for every app in parallel. The endpoint
  // returns { connected: false, … } for unconnected apps, so it's safe to
  // call for all and merge.
  const refreshGithub = useCallback(async (appIds: string[]) => {
    if (appIds.length === 0) return;
    const results = await Promise.all(
      appIds.map(async (appId) => {
        try {
          const res = await fetch(`/api/apps/${encodeURIComponent(appId)}/github`, {
            credentials: 'same-origin',
          });
          if (!res.ok) return [appId, null] as const;
          const conn = (await res.json()) as GithubConnection;
          return [appId, conn] as const;
        } catch {
          return [appId, null] as const;
        }
      }),
    );
    setGithubByApp((prev) => {
      const next = { ...prev };
      for (const [appId, conn] of results) {
        if (conn) next[appId] = conn;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (apps.length === 0) return;
    refreshGithub(apps.map((a) => a.id));
  }, [apps, refreshGithub]);

  // After OAuth callback, the user lands here with ?github=connected&appId=X.
  // Refresh just that app's connection state and toast briefly.
  const [githubToast, setGithubToast] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('github');
    const appId = params.get('appId');
    if (!status) return;
    if (status === 'connected' && appId) {
      refreshGithub([appId]);
      setGithubToast('GitHub repository connected. Open the app to start switching branches.');
    } else if (status === 'pending') {
      setGithubToast(
        'GitHub authorized. Open an app and pick a repository from its menu when ready.',
      );
    } else if (status === 'failed') {
      setGithubToast(`GitHub connect failed: ${params.get('reason') || 'unknown error'}`);
    }
    // Strip the query so refreshing the page doesn't keep re-firing.
    const url = new URL(window.location.href);
    url.searchParams.delete('github');
    url.searchParams.delete('reason');
    if (url.pathname.startsWith('/apps') || url.pathname.startsWith('/dashboard')) {
      // Keep ?appId out of the dashboard URL too — it's editor scope.
      url.searchParams.delete('appId');
    }
    window.history.replaceState(null, '', url.toString());
    const t = setTimeout(() => setGithubToast(null), 5000);
    return () => clearTimeout(t);
  }, [refreshGithub]);

  const handleGithubConnect = useCallback(async (app: AppRow) => {
    setGithubBusyAppId(app.id);
    try {
      // The OAuth App's registered redirect URL points at /github-callback,
      // which handles the exchange + repo picker in the SPA. We don't pass a
      // returnTo — /github-callback drives the post-auth navigation itself.
      const qs = new URLSearchParams({ appId: app.id });
      const res = await fetch(`/api/github/install-url?${qs}`, { credentials: 'same-origin' });
      if (!res.ok) {
        const body = await res.text();
        setError(`Could not start GitHub connect: ${body}`);
        setGithubBusyAppId(null);
        return;
      }
      const { url } = (await res.json()) as { url: string };
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
      setGithubBusyAppId(null);
    }
  }, []);

  const handleGithubDisconnect = useCallback(async (app: AppRow) => {
    setGithubBusyAppId(app.id);
    try {
      const res = await fetch(`/api/apps/${encodeURIComponent(app.id)}/github/disconnect`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) {
        const body = await res.text();
        setError(`Disconnect failed: ${body}`);
        return;
      }
      setGithubByApp((prev) => {
        const next = { ...prev };
        delete next[app.id];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setGithubBusyAppId(null);
    }
  }, []);

  /**
   * Push is critical — never fire the API directly from a menu item.
   * Open the confirmation modal so the user reviews the repo + branch
   * + commit message before anything leaves our system.
   */
  const handleGithubPush = useCallback((app: AppRow) => {
    setModal({ kind: 'github-push', app });
  }, []);

  const onOpen = (app: AppRow) =>
    window.location.assign(`/?platform=web&v2=true&appId=${encodeURIComponent(app.id)}`);

  const onDuplicate = async (app: AppRow) => {
    setError(null);
    try {
      const res = await fetch(`/api/apps/${app.id}/duplicate`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refresh();
    } catch (err) {
      setError(`Could not duplicate: ${err instanceof Error ? err.message : err}`);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter((a) => a.name.toLowerCase().includes(q));
  }, [apps, search]);

  return (
    <AppShell
      user={user}
      search={{ value: search, onChange: setSearch, placeholder: 'Search apps...' }}
      primaryAction={
        <button type="button" className="btn btn-p" onClick={() => setModal({ kind: 'create' })}>
          + New app
        </button>
      }
    >
      {error ? <div className="dash-error">{error}</div> : null}
      {githubToast ? (
        <div
          className="dash-error"
          style={{
            background: 'rgba(22,163,74,.10)',
            color: '#15803D',
            border: '1px solid rgba(22,163,74,.35)',
          }}
        >
          {githubToast}
        </div>
      ) : null}

      {loading ? (
        <div className="dash-empty">
          <div className="dash-empty-title">Loading…</div>
        </div>
      ) : apps.length === 0 ? (
        <div className="dash-empty">
          <div className="dash-empty-title">No apps yet</div>
          <p className="dash-empty-sub">
            Create your first app to start building. You can always rename, duplicate, or delete it
            later.
          </p>
          <button type="button" className="btn btn-p" onClick={() => setModal({ kind: 'create' })}>
            + Create your first app
          </button>
        </div>
      ) : (
        <div className="dash-grid">
          {filtered.map((app) => (
            <AppCard
              key={app.id}
              app={app}
              github={githubByApp[app.id]}
              githubBusy={githubBusyAppId === app.id}
              onOpen={() => onOpen(app)}
              onRename={() => setModal({ kind: 'rename', app })}
              onDuplicate={() => onDuplicate(app)}
              onDelete={() => setModal({ kind: 'delete', app })}
              onGithubConnect={() => handleGithubConnect(app)}
              onGithubDisconnect={() => setModal({ kind: 'github-disconnect', app })}
              onGithubPush={() => handleGithubPush(app)}
            />
          ))}
          <button
            type="button"
            className="dash-card dash-card-empty"
            onClick={() => setModal({ kind: 'create' })}
          >
            <div>
              <div className="dash-card-empty-plus">+</div>
              <div className="dash-card-empty-label">Create new app</div>
            </div>
          </button>
        </div>
      )}

      {modal.kind === 'create' ? (
        <CreateAppModal
          onClose={() => setModal({ kind: 'closed' })}
          onCreated={(app) => {
            setModal({ kind: 'closed' });
            // Files are already committed server-side from the chosen
            // blueprint, so the editor just loads the app like any other.
            const params = new URLSearchParams({
              platform: 'web',
              v2: 'true',
              appId: app.id,
            });
            window.location.assign(`/?${params.toString()}`);
          }}
        />
      ) : null}

      {modal.kind === 'rename' ? (
        <RenameAppModal
          app={modal.app}
          onClose={() => setModal({ kind: 'closed' })}
          onRenamed={() => {
            setModal({ kind: 'closed' });
            refresh();
          }}
        />
      ) : null}

      {modal.kind === 'delete' ? (
        <DeleteAppModal
          app={modal.app}
          onClose={() => setModal({ kind: 'closed' })}
          onDeleted={() => {
            setModal({ kind: 'closed' });
            refresh();
          }}
        />
      ) : null}

      {modal.kind === 'github-disconnect' ? (
        <ConfirmModal
          title="Disconnect GitHub?"
          body={
            <>
              <p>
                This app will no longer mirror to GitHub. The OAuth token will be deleted from our
                database — you can reconnect any time.
              </p>
              <p style={{ color: 'var(--t3)', fontSize: 12 }}>
                Releases already pushed to GitHub stay on GitHub. Only the connection from this app
                is removed.
              </p>
            </>
          }
          confirmLabel="Disconnect"
          danger
          onCancel={() => setModal({ kind: 'closed' })}
          onConfirm={async () => {
            await handleGithubDisconnect(modal.app);
            setModal({ kind: 'closed' });
          }}
        />
      ) : null}

      {modal.kind === 'github-push' ? (
        <PushToGithubModal
          appId={modal.app.id}
          appName={modal.app.name}
          defaultMessage={`tile-packet: update ${modal.app.name}`}
          onClose={() => setModal({ kind: 'closed' })}
          onSuccess={(res) => {
            setGithubToast(
              `Pushed ${res.fileCount} files to ${res.repo}@${res.branch} (${res.gitSha.slice(
                0,
                7,
              )}).`,
            );
          }}
        />
      ) : null}
    </AppShell>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* AppCard — single tile in the grid                                          */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Small status pill rendered on the corner of each app card preview.
 *
 * Three states, matching the policy "by default an app has 0 releases,
 * release is mandatory to go live":
 *   - LIVE      green   — at least one deployed release
 *   - PENDING   amber   — releases exist, none deployed
 *   - NOT LIVE  gray    — zero releases (new app default)
 */
function LiveBadge({ releaseCount, isLive }: { releaseCount: number; isLive: boolean }) {
  let bg = 'rgba(107, 114, 128, 0.92)'; // gray-500
  let label = 'Not live';
  let title = 'No releases yet. Create a release in the editor to deploy this app.';
  if (isLive) {
    bg = 'rgba(22, 163, 74, 0.94)'; // green-600
    label = 'Live';
    title = 'At least one release is deployed.';
  } else if (releaseCount > 0) {
    bg = 'rgba(245, 158, 11, 0.94)'; // amber-500
    label = 'Pending';
    title = `${releaseCount} release${
      releaseCount === 1 ? '' : 's'
    } created but none deployed yet.`;
  }
  return (
    <div
      title={title}
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        padding: '2px 8px',
        borderRadius: 999,
        background: bg,
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
        pointerEvents: 'none',
      }}
    >
      {label}
    </div>
  );
}

function AppCard({
  app,
  github,
  githubBusy,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
  onGithubConnect,
  onGithubDisconnect,
  onGithubPush,
}: {
  app: AppRow;
  github?: GithubConnection;
  githubBusy: boolean;
  onOpen: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onGithubConnect: () => void;
  onGithubDisconnect: () => void;
  onGithubPush: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Close kebab menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const handleAction = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    fn();
  };

  const isConnected = github?.connected === true;
  const hasOAuth = !!github && !github.connected && !!github.tokenScope; // OAuth done, no repo picked yet

  return (
    <div
      ref={cardRef}
      className="dash-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div
        className="dash-card-preview"
        style={{ background: gradientForId(app.id), position: 'relative' }}
        aria-hidden
      >
        <div style={{ fontSize: 36, opacity: 0.4 }}>
          {(app.name.trim() || 'Untitled app').charAt(0).toUpperCase()}
        </div>
        {/* Live-state badge — corner of the preview tile so it's
            visible even when the title is truncated. */}
        <LiveBadge releaseCount={app.releaseCount ?? 0} isLive={app.isLive ?? false} />
        {app.hasDrift && (
          <div
            title="A branch on this app has unreconciled drift between cloud and git. Open the app to resolve."
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              padding: '2px 8px',
              borderRadius: 999,
              background: 'rgba(245, 158, 11, 0.94)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
              pointerEvents: 'none',
            }}
          >
            ⚠ Drift
          </div>
        )}
      </div>

      <div className="dash-card-info">
        <h3 className="dash-card-name">{app.name.trim() || 'Untitled app'}</h3>
        <div className="dash-card-meta">Edited {fmtRelative(app.updatedAt)} · iOS + Android</div>
        {/* Release status hint. Hidden once the app is live and connected,
            because the GitHub repo line below already establishes "ready". */}
        {!(app.isLive && isConnected) && (
          <div
            className="dash-card-meta"
            style={{
              marginTop: 4,
              color: app.isLive
                ? '#15803D'
                : (app.releaseCount ?? 0) > 0
                ? '#B45309'
                : 'var(--t3, #6b7280)',
            }}
          >
            {app.isLive
              ? `● Live · ${app.deployedCount} deployed release${
                  app.deployedCount === 1 ? '' : 's'
                }`
              : (app.releaseCount ?? 0) > 0
              ? `● ${app.releaseCount} release${
                  app.releaseCount === 1 ? '' : 's'
                } · not deployed yet`
              : '○ Not live · open the editor to create a release'}
          </div>
        )}
        {isConnected ? (
          <div
            className="dash-card-meta"
            style={{
              marginTop: 4,
              color: '#15803D',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
            title={`GitHub repo: ${github!.repo}`}
          >
            <span aria-hidden>⎇</span>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: 'monospace',
                fontSize: 11,
              }}
            >
              {github!.repo}
            </span>
          </div>
        ) : hasOAuth ? (
          <div
            className="dash-card-meta"
            style={{ marginTop: 4, color: '#B45309' }}
            title="OAuth complete — open the app to pick a repository."
          >
            ⎇ pick a repo…
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className={`dash-card-kebab${menuOpen ? ' open' : ''}`}
        aria-label="App actions"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
      >
        ⋯
      </button>

      {menuOpen ? (
        <div className="dash-card-menu" onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={handleAction(onRename)}>
            Rename
          </button>
          <button type="button" onClick={handleAction(onDuplicate)}>
            Duplicate
          </button>
          {isConnected && (
            <button type="button" onClick={handleAction(onGithubPush)}>
              Push to {github!.repo.split('/')[1] || 'GitHub'}…
            </button>
          )}
          {isConnected || hasOAuth ? (
            <button type="button" disabled={githubBusy} onClick={handleAction(onGithubDisconnect)}>
              {githubBusy ? 'Disconnecting…' : 'Disconnect GitHub'}
            </button>
          ) : (
            <button type="button" disabled={githubBusy} onClick={handleAction(onGithubConnect)}>
              {githubBusy ? 'Opening GitHub…' : 'Connect GitHub…'}
            </button>
          )}
          <button type="button" className="danger" onClick={handleAction(onDelete)}>
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Modals                                                                     */
/* ────────────────────────────────────────────────────────────────────────── */

function Modal({
  children,
  onClose,
  className,
}: {
  children: ReactNode;
  onClose: () => void;
  className?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The modal lives inside `.tile-dash-host` so its scoped styles apply.
  return (
    <div className="dlg-backdrop" onMouseDown={onClose}>
      <div
        className={`dlg${className ? ` ${className}` : ''}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function CreateAppModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (app: AppRow) => void;
}) {
  const [blueprints, setBlueprints] = useState<BlueprintMetadata[]>([]);
  const [bpLoading, setBpLoading] = useState(true);
  const [bpError, setBpError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [blueprintId, setBlueprintId] = useState<string | null>(null);
  const [wireCodePush, setWireCodePush] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [createdApp, setCreatedApp] = useState<AppRow | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch blueprint catalog from tile-backend on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/blueprints', { credentials: 'same-origin' });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = (await res.json()) as { blueprints: BlueprintMetadata[] };
        if (cancelled) return;
        const list = data.blueprints ?? [];
        setBlueprints(list);
        // Default selection = first blueprint in the list.
        if (list.length > 0) setBlueprintId(list[0].id);
      } catch (e) {
        if (!cancelled) setBpError(e instanceof Error ? e.message : 'Could not load blueprints');
      } finally {
        if (!cancelled) setBpLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('App name is required.');
      return;
    }
    if (!blueprintId) {
      setError('Pick a blueprint to start from.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/apps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ name: name.trim(), blueprint: blueprintId, wireCodePush }),
      });
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        setError((data.error as string) || `Could not create app (HTTP ${res.status})`);
        return;
      }
      const app = (data as { app: AppRow }).app;
      const cp = data.codePush as
        | { requested?: boolean; wired?: boolean; warning?: string }
        | undefined;
      // Code Push was asked for but couldn't be wired: the app was still created (plain
      // blueprint, no broken JS) — surface the reason and let the user open it anyway.
      if (cp?.requested && !cp.wired) {
        setCreatedApp(app);
        setWarning(cp.warning || 'Code Push could not be wired for this blueprint.');
        return;
      }
      onCreated(app);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} className="dlg-create">
      <h2 className="dlg-title">New app</h2>
      {error ? (
        <div className="dash-error" style={{ margin: '0 0 12px' }}>
          {error}
        </div>
      ) : null}
      {warning ? (
        <div
          className="dash-error"
          style={{
            margin: '0 0 12px',
            background: 'rgba(217,119,6,.1)',
            color: '#92400e',
            border: '1px solid rgba(217,119,6,.3)',
          }}
        >
          App created, but Code Push wasn’t wired: {warning} You can open it and wire OTA later.
        </div>
      ) : null}
      <form onSubmit={onSubmit}>
        <p className="dlg-section-label">Choose a blueprint</p>
        {bpLoading ? (
          <div className="bp-loading">Loading blueprints…</div>
        ) : bpError ? (
          <div className="dash-error" style={{ margin: '0 0 12px' }}>
            {bpError}
          </div>
        ) : blueprints.length === 0 ? (
          <div className="dash-error" style={{ margin: '0 0 12px' }}>
            No blueprints available. Run the seedBlueprints script in tile-backend.
          </div>
        ) : (
          <div className="bp-grid" role="radiogroup" aria-label="Blueprint">
            {blueprints.map((bp) => {
              const selected = bp.id === blueprintId;
              return (
                <button
                  key={bp.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`bp-card${selected ? ' is-selected' : ''}`}
                  onClick={() => setBlueprintId(bp.id)}
                >
                  <div
                    className="bp-card-preview"
                    style={bp.accent ? { background: bp.accent } : undefined}
                    aria-hidden
                  >
                    <span>{bp.emoji ?? '📦'}</span>
                  </div>
                  <span className="bp-card-name">{bp.name}</span>
                  {bp.description ? <span className="bp-card-desc">{bp.description}</span> : null}
                </button>
              );
            })}
          </div>
        )}

        <p className="dlg-section-label">App name</p>
        <input
          autoFocus
          type="text"
          placeholder="My awesome app"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="dlg-input"
        />

        {/* Code Push tick — server injects the tile-push OTA stack into the first save. */}
        <label
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            marginTop: 16,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={wireCodePush}
            onChange={(e) => setWireCodePush(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            <span style={{ fontWeight: 600, display: 'block' }}>⚡ Wire Code Push (OTA)</span>
            <span
              style={{
                fontSize: 12,
                color: 'var(--t2, #6b7280)',
                lineHeight: 1.45,
                display: 'block',
                marginTop: 2,
              }}
            >
              Injects the tile-push stack (app entry, deps, config, fingerprint plugin) so you can
              ship JS updates over-the-air out of the box.
            </span>
          </span>
        </label>

        <div className="dlg-actions">
          {createdApp ? (
            <button type="button" onClick={() => onCreated(createdApp)} className="btn btn-p">
              Open app →
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="btn btn-s">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || bpLoading || !blueprintId}
                className="btn btn-p"
              >
                {submitting ? 'Creating…' : 'Create'}
              </button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}

function RenameAppModal({
  app,
  onClose,
  onRenamed,
}: {
  app: AppRow;
  onClose: () => void;
  onRenamed: () => void;
}) {
  const [name, setName] = useState(app.name);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError('App name is required.');
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/apps/${app.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        setError((data.error as string) || `HTTP ${res.status}`);
        return;
      }
      onRenamed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="dlg-title">Rename app</h2>
      {error ? (
        <div className="dash-error" style={{ margin: '0 0 12px' }}>
          {error}
        </div>
      ) : null}
      <form onSubmit={onSubmit}>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="dlg-input"
        />
        <div className="dlg-actions">
          <button type="button" onClick={onClose} className="btn btn-s">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="btn btn-p">
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteAppModal({
  app,
  onClose,
  onDeleted,
}: {
  app: AppRow;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/apps/${app.id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        setError((data.error as string) || `HTTP ${res.status}`);
        return;
      }
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="dlg-title">Delete app?</h2>
      {error ? (
        <div className="dash-error" style={{ margin: '0 0 12px' }}>
          {error}
        </div>
      ) : null}
      <p className="dlg-text">
        This will permanently delete <strong>{app.name}</strong> and all its files. This cannot be
        undone.
      </p>
      <div className="dlg-actions">
        <button type="button" onClick={onClose} className="btn btn-s">
          Cancel
        </button>
        <button type="button" onClick={onConfirm} disabled={submitting} className="btn btn-d">
          {submitting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </Modal>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Generic confirm dialog                                                     */
/* ────────────────────────────────────────────────────────────────────────── */

function ConfirmModal({
  title,
  body,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const handle = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal onClose={onCancel}>
      <h2 className="dlg-title">{title}</h2>
      <div className="dlg-text">{body}</div>
      <div className="dlg-actions">
        <button type="button" onClick={onCancel} className="btn btn-s" disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          onClick={handle}
          disabled={submitting}
          className={danger ? 'btn btn-d' : 'btn btn-p'}
        >
          {submitting ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Default export — auth-guarded                                              */
/* ────────────────────────────────────────────────────────────────────────── */

export default function AppsList() {
  return <AuthRoute>{(user) => <AppsListInner user={user} />}</AuthRoute>;
}
